// Account / Security Settings — v1.1.0 (Billing + TOTP)
//
// User can view their account details, manage their subscription, and
// manage MFA factors.
// - Billing: plan status, trial countdown, upgrade / manage billing
// - Security: enrolled authenticators, Add / Remove / Reset 2FA, recovery codes
// - Danger zone: sign out
//
// Protected route — only signed-in users can access this.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { supabase } from '../lib/supabaseClient';
import { API_BASE_URL } from '../services/api';
import { getPlan } from '../lib/planCatalog';
import {
  hasUpgradeIntent,
  hasUpgradeParam,
  isUpgradeConfirmed,
  stripUpgradedParam,
} from '../lib/upgradeNotice';
import TotpEnrollmentModal from '../components/TotpEnrollmentModal';

// =========================
// HELPERS
// =========================

// Friendly label for a factor when Supabase doesn't return a friendly_name.
function factorLabel(factor) {
  if (factor?.friendly_name) return factor.friendly_name;
  if (factor?.factor_type === 'totp') return 'Authenticator app';
  if (factor?.factor_type === 'webauthn') return 'Passkey';
  if (factor?.factor_type === 'phone') return 'Phone';
  return 'Authenticator';
}

// =========================
// SECTION LABEL — small caps eyebrow
// =========================
function SectionLabel({ children, style }) {
  return (
    <div
      style={{
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--weight-bold)',
        letterSpacing: 'var(--tracking-label)',
        textTransform: 'uppercase',
        color: 'var(--color-muted)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// =========================
// SECTION HEADING — h2-style section title inside a card
// =========================
function SectionHeading({ children }) {
  return (
    <h2
      style={{
        fontFamily: 'var(--font-display)',
        fontSize: 'var(--text-2xl)',
        fontWeight: 'var(--weight-bold)',
        color: 'var(--color-white)',
        margin: 0,
        marginBottom: 'var(--space-4)',
        paddingBottom: 'var(--space-3)',
        borderBottom: '1px solid var(--color-navy-border)',
        textTransform: 'uppercase',
        letterSpacing: 'var(--tracking-tight)',
      }}
    >
      {children}
    </h2>
  );
}

// =========================
// INLINE NOTE — inline error/success banner inside a card
// =========================
function InlineNote({ tone = 'success', children, style }) {
  const palette = tone === 'error'
    ? {
        bg: 'var(--color-danger-ghost)',
        border: 'var(--color-danger)',
        color: 'var(--color-danger-lt)',
      }
    : {
        bg: 'var(--color-success-ghost)',
        border: 'var(--color-success)',
        color: 'var(--color-success-lt)',
      };
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      style={{
        padding: 'var(--space-3) var(--space-4)',
        borderRadius: 'var(--radius-md)',
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        color: palette.color,
        fontSize: 'var(--text-base)',
        fontWeight: 'var(--weight-semibold)',
        lineHeight: 'var(--leading-snug)',
        whiteSpace: 'pre-wrap',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// =========================
// INFO ROW — label : value pair separated by a hairline
// =========================
function InfoRow({ label, children, last }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 'var(--space-3)',
        padding: 'var(--space-3) 0',
        borderBottom: last ? 'none' : '1px solid var(--color-navy-border)',
      }}
    >
      <span
        style={{
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--weight-semibold)',
          color: 'var(--color-steel)',
          letterSpacing: 'var(--tracking-wide)',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 'var(--text-base)',
          fontWeight: 'var(--weight-semibold)',
          color: 'var(--color-white)',
          textAlign: 'right',
          wordBreak: 'break-word',
        }}
      >
        {children}
      </span>
    </div>
  );
}

// =========================
// MAIN PAGE
// =========================
export default function Account() {
  const {
    user,
    listMfaFactors,
    removeMfaFactor,
    getRecoveryCodeStatus,
    generateRecoveryCodes,
    signOut,
  } = useAuth();
  const {
    trialDaysLeft,
    isTrialing,
    isPro,
    startCheckout,
    openPortal,
    loading: subscriptionLoading,
    status: subscriptionStatus,
    tier: subscriptionTier,
  } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();

  // Billing action state
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError]     = useState(null);
  // BL-053 Story 53.1 — the Stripe success_url marker (?upgraded=true) is an
  // untrusted intent signal, never proof of payment (ADR-072 §4). It starts
  // false and is only ever set by the single authoritative evaluation below.
  const [upgradedNotice, setUpgradedNotice] = useState(false);
  const [upgradedTier, setUpgradedTier]     = useState(null);
  const [factors, setFactors] = useState([]);
  const [loadingFactors, setLoadingFactors] = useState(true);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [removingFactorId, setRemovingFactorId] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [resetNotice, setResetNotice] = useState(null); // { tone, text }
  const [recoveryStatus, setRecoveryStatus] = useState(null); // { total, remaining }
  const [recoveryBanner, setRecoveryBanner] = useState(null); // text or null
  const [regenerating, setRegenerating] = useState(false);
  const [regeneratedCodes, setRegeneratedCodes] = useState(null); // { codes: string[] } | null
  const [regenerateError, setRegenerateError] = useState('');
  const [signingOut, setSigningOut] = useState(false);

  // Stable factor loader so useEffect/useCallback don't re-fire endlessly.
  const refreshFactors = useCallback(async () => {
    const { data } = await listMfaFactors();
    // Show only verified factors as "enrolled" so the user isn't confused
    // by half-finished enrollment artifacts. The enrollment modal cleans
    // these up automatically, but a hard refresh between attempts could
    // expose one momentarily.
    const verified = (data || []).filter((f) => f?.status === 'verified');
    setFactors(verified);
  }, [listMfaFactors]);

  // Pull recovery-code status (N of 10 remaining) for the security UI.
  const refreshRecoveryStatus = useCallback(async () => {
    const { data, error } = await getRecoveryCodeStatus();
    if (error) {
      setRecoveryStatus(null);
      return;
    }
    setRecoveryStatus({
      total: data?.total ?? 0,
      remaining: data?.remaining ?? 0,
    });
  }, [getRecoveryCodeStatus]);

  // Initial load — factors then recovery-code status, in sequence.
  //
  // These were previously fired in Promise.all(), but both call into
  // supabase.auth.* paths (listFactors + getSession via callMfaApi) which
  // share the GoTrue lock. Firing them in the same tick races the lock and
  // surfaces as "Lock was stolen by another request" on the Account page.
  // Sequencing them costs ~one extra round-trip on first load and removes
  // the race entirely.
  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoadingFactors(true);
      try {
        await refreshFactors();
        if (!mounted) return;
        await refreshRecoveryStatus();
      } finally {
        if (mounted) setLoadingFactors(false);
      }
    }
    if (user) load();
    return () => {
      mounted = false;
    };
  }, [user, refreshFactors, refreshRecoveryStatus]);

  // Surface the "you just used a recovery code" banner from the
  // challenge page. Strip the state from history so a back/forward
  // navigation doesn't replay it. Runs once on mount only.
  useEffect(() => {
    if (location.state?.recoveryUsed && location.state?.recoveryNotice) {
      setRecoveryBanner(location.state.recoveryNotice);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []); // eslint-disable-line

  // ── Upgrade-success notice — BL-053 Story 53.1 / ADR-072 §4 ─────────────
  //
  // `?upgraded=true` says only "this navigation came from a checkout attempt."
  // It is not proof of payment or entitlement, so it is evaluated exactly once
  // against the authoritative profile projection and then consumed:
  //
  //   1. wait until SubscriptionContext has finished its first profile read —
  //      evaluating early would fail closed against a not-yet-loaded profile
  //      and burn the marker before the answer is knowable;
  //   2. show success only for `active` + a canonical paid tier;
  //   3. strip the marker with replace semantics whether the state confirmed
  //      or rejected the upgrade, so refresh / back / forward cannot recreate
  //      the message from the URL.
  //
  // The marker never mutates profile, entitlement, Stripe, or billing state —
  // it only decides whether a message the server already justified is shown.
  const upgradeEvaluated = useRef(false);
  useEffect(() => {
    if (upgradeEvaluated.current) return;
    if (!hasUpgradeParam(location.search)) return;
    if (subscriptionLoading) return; // (1) authoritative state not in yet

    upgradeEvaluated.current = true;

    // (2) state decides — the marker only gates whether we look.
    const confirmed =
      hasUpgradeIntent(location.search) &&
      isUpgradeConfirmed({ status: subscriptionStatus, tier: subscriptionTier });
    setUpgradedNotice(confirmed);
    setUpgradedTier(confirmed ? subscriptionTier : null);

    // (3) consume it, preserving any unrelated query parameters.
    navigate(
      { pathname: location.pathname, search: stripUpgradedParam(location.search) },
      { replace: true }
    );
  }, [
    subscriptionLoading,
    subscriptionStatus,
    subscriptionTier,
    location.pathname,
    location.search,
    navigate,
  ]);

  async function handleRemoveFactor(factorId) {
    if (
      !window.confirm(
        'Remove this authenticator? You will need to re-enroll it if you lose access to a backup method.'
      )
    ) {
      return;
    }

    setRemovingFactorId(factorId);
    const { error } = await removeMfaFactor(factorId);
    setRemovingFactorId(null);

    if (error) {
      alert(`Error removing factor: ${error.message}`);
      return;
    }

    await Promise.all([refreshFactors(), refreshRecoveryStatus()]);
  }

  async function handleEnrollmentComplete() {
    setShowEnrollModal(false);
    // Supabase reports the new factor immediately, but give it a quick
    // beat so the listFactors() call reflects the verified status.
    setTimeout(() => {
      refreshFactors();
      refreshRecoveryStatus();
    }, 300);
  }

  function handleModalClose() {
    setShowEnrollModal(false);
    setTimeout(() => {
      refreshFactors();
      refreshRecoveryStatus();
    }, 300);
  }

  // Issue a fresh batch of 10 recovery codes, invalidating the old
  // batch. The new codes are shown ONCE in a modal-style panel so the
  // user can copy / download them.
  async function handleRegenerateCodes() {
    if (
      !window.confirm(
        'Generate a fresh set of recovery codes?\n\nYour current recovery codes will stop working immediately. Save the new codes somewhere safe.'
      )
    ) {
      return;
    }

    setRegenerating(true);
    setRegenerateError('');

    const { data, error } = await generateRecoveryCodes();

    setRegenerating(false);

    if (error) {
      setRegenerateError(
        error.message || 'Could not generate new recovery codes. Try again.'
      );
      return;
    }

    setRegeneratedCodes({ codes: data?.codes || [] });
    refreshRecoveryStatus();
  }

  function dismissRegeneratedCodes() {
    setRegeneratedCodes(null);
  }

  function copyCodes(codes) {
    navigator.clipboard.writeText(codes.join('\n')).catch(() => {});
  }

  function downloadCodes(codes) {
    const header = `Rentava — MFA recovery codes
Account: ${user?.email || 'unknown'}
Generated: ${new Date().toLocaleString()}

Each code can be used ONCE. Using a code will deactivate your
authenticator app — you'll need to set up a new one afterwards.

`;
    const blob = new Blob([header + codes.join('\n') + '\n'], {
      type: 'text/plain',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rentava-recovery-codes-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Server-side escape hatch — deletes every MFA factor on the current
  // user via the mfa-reset Netlify function (which uses the service role
  // key). Used when an in-browser cleanup can't see/remove a stuck
  // factor, e.g. because the session is at aal1 and Supabase is hiding
  // the factor from listFactors().
  async function handleResetMfa() {
    if (
      !window.confirm(
        'Remove ALL two-factor authentication factors on your account?\n\nYou will need to re-enroll an authenticator app after this. Only do this if enrollment is stuck.'
      )
    ) {
      return;
    }

    setResetting(true);
    setResetNotice(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) {
        setResetNotice({
          tone: 'error',
          text: 'Could not read your current session. Sign out, sign in, and try again.',
        });
        setResetting(false);
        return;
      }

      // Route through the project's API_BASE_URL — same pattern the rest
      // of the app uses for Netlify functions.
      const response = await fetch(`${API_BASE_URL}/mfa-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const payload = await response.json().catch(() => null);

      if (!response.ok || payload?.success === false) {
        setResetNotice({
          tone: 'error',
          text:
            payload?.error ||
            `Reset failed (HTTP ${response.status}). Try again, and if it keeps failing, message support.`,
        });
        setResetting(false);
        return;
      }

      setResetNotice({
        tone: 'success',
        text:
          payload?.message ||
          'All MFA factors removed. You can enroll a new authenticator now.',
      });
      await Promise.all([refreshFactors(), refreshRecoveryStatus()]);
    } catch (err) {
      setResetNotice({
        tone: 'error',
        text: err?.message || 'Network error while resetting 2FA.',
      });
    } finally {
      setResetting(false);
    }
  }

  async function handleSignOut() {
    setSigningOut(true);
    await signOut();
    navigate('/', { replace: true });
  }

  const pageWrap = {
    background: 'var(--color-navy)',
    minHeight: '100%',
    padding: 'var(--space-6)',
    color: 'var(--color-white)',
    fontFamily: 'var(--font-body)',
  };

  if (!user) {
    return (
      <div style={pageWrap}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <div className="card">
            <p style={{ color: 'var(--color-steel-lt)', margin: 0 }}>
              Loading account information…
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageWrap}>
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        {/* ── Page header ───────────────────────────────────── */}
        <header style={{ marginBottom: 'var(--space-6)' }}>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-4xl)',
              fontWeight: 'var(--weight-black)',
              letterSpacing: 'var(--tracking-tight)',
              textTransform: 'uppercase',
              color: 'var(--color-white)',
              margin: 0,
              marginBottom: 'var(--space-2)',
            }}
          >
            Account Settings
          </h1>
          <p
            style={{
              fontSize: 'var(--text-md)',
              color: 'var(--color-steel-lt)',
              margin: 0,
            }}
          >
            Manage your subscription, security, and account.
          </p>
        </header>

        {/* ── Subscription hero card — promoted to first section ── */}
        <SubscriptionCard
          isPro={isPro}
          isTrialing={isTrialing}
          trialDaysLeft={trialDaysLeft}
          upgradedNotice={upgradedNotice}
          upgradedTier={upgradedTier}
          dismissUpgradedNotice={() => setUpgradedNotice(false)}
          billingLoading={billingLoading}
          billingError={billingError}
          onUpgrade={async () => {
            setBillingError(null);
            setBillingLoading(true);
            try { await startCheckout(); }
            catch (err) { setBillingError(err.message); setBillingLoading(false); }
          }}
          onManage={async () => {
            setBillingError(null);
            setBillingLoading(true);
            try { await openPortal(); }
            catch (err) { setBillingError(err.message); setBillingLoading(false); }
          }}
          onViewPricing={() => navigate('/pricing')}
        />

        {/* ── Profile ───────────────────────────────────────── */}
        <section className="card" style={{ marginBottom: 'var(--space-5)' }}>
          <SectionHeading>Profile</SectionHeading>
          <InfoRow label="Email" last>{user.email}</InfoRow>
        </section>

        {/* ── Security ──────────────────────────────────────── */}
        <section className="card" style={{ marginBottom: 'var(--space-5)' }}>
          <SectionHeading>Security</SectionHeading>

          {recoveryBanner && (
            <InlineNote tone="success" style={{ marginBottom: 'var(--space-4)' }}>
              {recoveryBanner}
            </InlineNote>
          )}

          {/* — Two-Factor Authentication — */}
          <div style={{ marginBottom: 'var(--space-6)' }}>
            <SectionLabel style={{ marginBottom: 'var(--space-2)' }}>
              Two-Factor Authentication
            </SectionLabel>
            <p
              style={{
                fontSize: 'var(--text-base)',
                color: 'var(--color-steel-lt)',
                margin: 0,
                marginBottom: 'var(--space-4)',
                lineHeight: 'var(--leading-normal)',
              }}
            >
              Add an authenticator app (Google Authenticator, 1Password, Authy, etc.) to strengthen
              your account security.
            </p>

            {/* Enrolled Factors */}
            {!loadingFactors && factors.length > 0 ? (
              <>
                <SectionLabel style={{ marginBottom: 'var(--space-2)' }}>
                  Enrolled Authenticators ({factors.length})
                </SectionLabel>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-3)',
                    marginBottom: 'var(--space-4)',
                  }}
                >
                  {factors.map((factor) => {
                    const enrolledDate = factor.created_at
                      ? new Date(factor.created_at).toLocaleDateString()
                      : 'Unknown';
                    const isVerified = factor?.status === 'verified';
                    return (
                      <div
                        key={factor.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 'var(--space-3)',
                          background: 'var(--color-navy)',
                          padding: 'var(--space-3) var(--space-4)',
                          borderRadius: 'var(--radius-md)',
                          border: '1px solid var(--color-navy-border)',
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: 'var(--text-base)',
                              color: 'var(--color-white)',
                              fontWeight: 'var(--weight-semibold)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'var(--space-2)',
                              flexWrap: 'wrap',
                            }}
                          >
                            {factorLabel(factor)}
                            <span className={`badge ${isVerified ? 'badge-active' : 'badge-reviewing'}`}>
                              {isVerified ? 'Active' : 'Pending'}
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: 'var(--text-sm)',
                              color: 'var(--color-muted)',
                              marginTop: 'var(--space-1)',
                            }}
                          >
                            Enrolled: {enrolledDate}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveFactor(factor.id)}
                          disabled={removingFactorId === factor.id}
                          className="btn btn-danger btn-sm"
                        >
                          {removingFactorId === factor.id ? 'Removing…' : 'Remove'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : loadingFactors ? (
              <EmptyInset>Loading authenticators…</EmptyInset>
            ) : (
              <EmptyInset>No authenticators enrolled yet.</EmptyInset>
            )}

            {/* Add Button + Reset escape hatch */}
            <div
              style={{
                display: 'flex',
                gap: 'var(--space-3)',
                alignItems: 'center',
                flexWrap: 'wrap',
                marginTop: 'var(--space-4)',
              }}
            >
              <button
                type="button"
                onClick={() => setShowEnrollModal(true)}
                disabled={showEnrollModal || resetting}
                className="btn btn-primary"
              >
                {showEnrollModal ? 'Opening…' : 'Add Authenticator App'}
              </button>
              <button
                type="button"
                onClick={handleResetMfa}
                disabled={resetting || showEnrollModal}
                className="btn btn-ghost btn-sm"
                title="Removes all MFA factors from your account on the server. Use when enrollment is stuck."
              >
                {resetting ? 'Resetting…' : 'Reset 2FA'}
              </button>
            </div>

            {resetNotice && (
              <InlineNote tone={resetNotice.tone} style={{ marginTop: 'var(--space-3)' }}>
                {resetNotice.text}
              </InlineNote>
            )}

            <div
              style={{
                marginTop: 'var(--space-4)',
                fontSize: 'var(--text-sm)',
                color: 'var(--color-muted)',
                background: 'var(--color-navy)',
                border: '1px solid var(--color-navy-border)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-3) var(--space-4)',
                lineHeight: 'var(--leading-normal)',
              }}
            >
              If enrollment ever gets stuck on "factor already exists," use{' '}
              <strong style={{ color: 'var(--color-amber)' }}>Reset 2FA</strong> to clear the server-side state,
              then enroll again.
            </div>
          </div>

          {/* — Recovery codes — shown only when a verified factor is enrolled — */}
          {factors.length > 0 && (
            <div style={{ marginBottom: 'var(--space-6)' }}>
              <SectionLabel style={{ marginBottom: 'var(--space-2)' }}>
                Recovery codes
              </SectionLabel>
              <p
                style={{
                  fontSize: 'var(--text-base)',
                  color: 'var(--color-steel-lt)',
                  margin: 0,
                  marginBottom: 'var(--space-3)',
                  lineHeight: 'var(--leading-normal)',
                }}
              >
                One-time codes you can use at sign-in if you lose access to your
                authenticator app. Using one will remove your authenticator and
                invalidate the rest — you'll set up a new authenticator afterwards.
              </p>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  flexWrap: 'wrap',
                  marginBottom: 'var(--space-2)',
                }}
              >
                <div style={{ fontSize: 'var(--text-base)', color: 'var(--color-white)' }}>
                  {recoveryStatus
                    ? recoveryStatus.total === 0
                      ? 'No recovery codes on file.'
                      : (
                        <>
                          <strong style={{ color: 'var(--color-amber)' }}>
                            {recoveryStatus.remaining}
                          </strong>
                          {' of '}
                          {recoveryStatus.total}
                          {' codes remaining'}
                        </>
                      )
                    : 'Loading…'}
                </div>
                <button
                  type="button"
                  onClick={handleRegenerateCodes}
                  disabled={regenerating}
                  className="btn btn-ghost btn-sm"
                  title="Generate a fresh set of 10 codes. Your current codes will stop working."
                >
                  {regenerating
                    ? 'Generating…'
                    : recoveryStatus?.total
                    ? 'Regenerate codes'
                    : 'Generate codes'}
                </button>
              </div>

              {regenerateError && (
                <InlineNote tone="error" style={{ marginTop: 'var(--space-3)' }}>
                  {regenerateError}
                </InlineNote>
              )}
            </div>
          )}

          {/* — Passkeys placeholder — foundation for v0.7.x+ — */}
          <div>
            <SectionLabel style={{ marginBottom: 'var(--space-2)' }}>
              Passkeys
            </SectionLabel>
            <p
              style={{
                fontSize: 'var(--text-base)',
                color: 'var(--color-steel-lt)',
                margin: 0,
                marginBottom: 'var(--space-3)',
                lineHeight: 'var(--leading-normal)',
              }}
            >
              Passkey (WebAuthn) sign-in will replace passwords with a secure
              device-based check.
            </p>
            <EmptyInset>
              Coming soon — Supabase WebAuthn is still in experimental release.
              We'll enable it here as soon as it ships GA.
            </EmptyInset>
          </div>
        </section>

        {/* ── Danger zone — pushed to the bottom, visually separated ── */}
        <section
          className="card"
          style={{
            marginBottom: 'var(--space-5)',
            borderColor: 'var(--color-danger)',
          }}
        >
          <SectionHeading>Danger zone</SectionHeading>
          <p
            style={{
              fontSize: 'var(--text-base)',
              color: 'var(--color-steel-lt)',
              margin: 0,
              marginBottom: 'var(--space-4)',
              lineHeight: 'var(--leading-normal)',
            }}
          >
            Sign out of this device. Your saved analyses and leads are kept on
            the server and will be there next time you sign in.
          </p>
          <button
            type="button"
            onClick={handleSignOut}
            disabled={signingOut}
            className="btn btn-danger"
          >
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </section>
      </div>

      {/* TOTP Enrollment Modal */}
      {showEnrollModal && (
        <TotpEnrollmentModal
          onClose={handleModalClose}
          onEnrollmentComplete={handleEnrollmentComplete}
        />
      )}

      {/* Regenerated recovery codes overlay — shown once, then dismissed */}
      {regeneratedCodes && (
        <RecoveryCodesOverlay
          codes={regeneratedCodes.codes}
          onCopy={() => copyCodes(regeneratedCodes.codes)}
          onDownload={() => downloadCodes(regeneratedCodes.codes)}
          onDismiss={dismissRegeneratedCodes}
        />
      )}
    </div>
  );
}

// =========================
// SUBSCRIPTION CARD — promoted hero, answer-first
// =========================
function SubscriptionCard({
  isPro,
  isTrialing,
  trialDaysLeft,
  upgradedNotice,
  upgradedTier,
  dismissUpgradedNotice,
  billingLoading,
  billingError,
  onUpgrade,
  onManage,
  onViewPricing,
}) {
  const planName = isPro ? 'Investor' : isTrialing ? 'Free Trial' : 'Free';
  const statusBadgeClass = isPro
    ? 'badge-active'
    : isTrialing
    ? 'badge-new'
    : 'badge-pass';
  const statusLabel = isPro ? 'Active' : isTrialing ? 'Trial' : 'Expired';

  // Trial urgency — flag the last 3 days so the user sees the runway clearly.
  const trialUrgent = isTrialing && trialDaysLeft <= 3;
  const trialMsg = isTrialing
    ? trialDaysLeft === 0
      ? 'Ends today'
      : trialDaysLeft === 1
      ? '1 day remaining'
      : `${trialDaysLeft} days remaining`
    : null;

  return (
    <section
      className="card-accent"
      style={{ marginBottom: 'var(--space-5)' }}
    >
      <SectionHeading>Subscription</SectionHeading>

      {upgradedNotice && (
        <InlineNote tone="success" style={{ marginBottom: 'var(--space-4)' }}>
          {/* Names the tier the authoritative profile actually holds — the
              banner must never assert a plan the customer is not on. */}
          🎉 You're now on {getPlan(upgradedTier)?.label || 'your new plan'}! Your
          subscription is active. Welcome aboard.{' '}
          <button
            type="button"
            onClick={dismissUpgradedNotice}
            aria-label="Dismiss"
            style={{
              float: 'right',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-success-lt)',
              fontWeight: 'var(--weight-bold)',
              fontSize: 'var(--text-base)',
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </InlineNote>
      )}

      {/* Plan headline */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
          marginBottom: 'var(--space-3)',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-3xl)',
            fontWeight: 'var(--weight-black)',
            color: 'var(--color-white)',
            lineHeight: 'var(--leading-none)',
          }}
        >
          {planName}
        </div>
        <span className={`badge ${statusBadgeClass}`}>{statusLabel}</span>
      </div>

      {/* Trial countdown — extra urgency when ≤3 days */}
      {trialMsg && (
        <div
          style={{
            fontSize: 'var(--text-base)',
            fontWeight: 'var(--weight-semibold)',
            color: trialUrgent ? 'var(--color-amber)' : 'var(--color-steel-lt)',
            marginBottom: 'var(--space-4)',
          }}
        >
          Trial · {trialMsg}
          {trialUrgent && (
            <span style={{ color: 'var(--color-amber)' }}>
              {' — upgrade to keep full access.'}
            </span>
          )}
        </div>
      )}

      {billingError && (
        <InlineNote tone="error" style={{ marginBottom: 'var(--space-3)' }}>
          {billingError}
        </InlineNote>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        {!isPro ? (
          <button
            type="button"
            className="btn btn-primary"
            disabled={billingLoading}
            onClick={onUpgrade}
          >
            {billingLoading ? 'Redirecting…' : 'Upgrade to Investor →'}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary"
            disabled={billingLoading}
            onClick={onManage}
          >
            {billingLoading ? 'Redirecting…' : 'Manage Billing'}
          </button>
        )}
        {!isPro && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onViewPricing}
          >
            View pricing
          </button>
        )}
      </div>
    </section>
  );
}

// =========================
// EMPTY INSET — subtle inset placeholder ("Loading…", "Coming soon", etc.)
// =========================
function EmptyInset({ children }) {
  return (
    <div
      style={{
        fontSize: 'var(--text-base)',
        color: 'var(--color-steel)',
        padding: 'var(--space-4)',
        background: 'var(--color-navy)',
        borderRadius: 'var(--radius-md)',
        border: '1px dashed var(--color-navy-border)',
        textAlign: 'center',
        lineHeight: 'var(--leading-normal)',
      }}
    >
      {children}
    </div>
  );
}

// =========================
// RECOVERY CODES OVERLAY — shown once after regeneration
// =========================
function RecoveryCodesOverlay({ codes, onCopy, onDownload, onDismiss }) {
  return (
    <div
      onClick={onDismiss}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'color-mix(in srgb, var(--color-navy) 85%, transparent)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 'var(--space-5)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card-elevated"
        style={{
          width: '100%',
          maxWidth: 460,
          maxHeight: '90vh',
          overflow: 'auto',
          color: 'var(--color-white)',
          fontFamily: 'var(--font-body)',
        }}
      >
        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-2xl)',
            fontWeight: 'var(--weight-black)',
            color: 'var(--color-white)',
            textTransform: 'uppercase',
            letterSpacing: 'var(--tracking-tight)',
            margin: 0,
            marginBottom: 'var(--space-2)',
          }}
        >
          Your new recovery codes
        </h2>
        <p
          style={{
            fontSize: 'var(--text-base)',
            color: 'var(--color-steel-lt)',
            margin: 0,
            marginBottom: 'var(--space-4)',
            lineHeight: 'var(--leading-normal)',
          }}
        >
          Save these now — your previous recovery codes have just been
          invalidated and you won't see this list again.
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 'var(--space-2)',
            padding: 'var(--space-4)',
            background: 'var(--color-navy)',
            border: '1px solid var(--color-navy-border)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--space-4)',
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--text-base)',
            color: 'var(--color-white)',
          }}
        >
          {codes.map((rc) => (
            <div
              key={rc}
              style={{
                textAlign: 'center',
                padding: 'var(--space-2) var(--space-1)',
                letterSpacing: 'var(--tracking-wide)',
              }}
            >
              {rc}
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ flex: 1 }}
            onClick={onCopy}
          >
            Copy
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ flex: 1 }}
            onClick={onDownload}
          >
            Download .txt
          </button>
        </div>

        <button
          type="button"
          className="btn btn-primary"
          style={{ width: '100%' }}
          onClick={onDismiss}
        >
          I've saved my codes
        </button>
      </div>
    </div>
  );
}
